import { Loader2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/shared/ui/button";

import type { AcqCatalogView } from "../../api/acqService";

/**
 * 「推荐是从哪儿来的」。
 *
 * ★★ 这一条不是装饰:推荐档是从**知识库 + 行业包 + 画像**推的,三样都没有时
 *   推出来的全是「未推断」。实测 33 个租户绝大多数 KB=0 ⇒ **一屏灰卡是多数人第一次看到的画面**。
 *   不写清"因为没料",用户只会以为功能坏了,然后一遍遍点那个永远不会成功的按钮。
 * ★ 所以三样料**一样一样地报数**(知识库 N 篇 / 行业包有无 / 画像有无),
 *   而不是笼统一句「AI 已为你推荐」—— 后者在 0 篇的时候也一样说得出口。
 */
export function RecommendBanner({
  view,
  recommending,
  onRecommend,
}: {
  view: AcqCatalogView;
  recommending: boolean;
  onRecommend: () => void;
}) {
  const { kbDocs, industryPack, icp } = view.inferredFrom;
  const noFuel = kbDocs === 0 && !industryPack && !icp;

  return (
    <section className="rounded-xl border bg-card px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm font-semibold">
            <Sparkles className="h-4 w-4 shrink-0 text-primary" />
            推荐来自：
            <span className="font-normal text-muted-foreground">
              知识库 {kbDocs} 篇 · 行业包
              {industryPack ? "已铸" : "未铸"} · 画像{icp ? "已确认" : "未确认"}
            </span>
          </p>
          <p className="mt-1 text-2xs text-muted-foreground">
            {view.recommendedAt
              ? `上次推断：${view.recommendedAt.replace("T", " ").slice(0, 16)}`
              : "还没推断过"}
            {" · 你手动改过关键词和推荐档的那几种，重新推断不会覆盖"}
          </p>
          {/* ★★ 上限要在**订阅之前**就说出来(契约 §13.1):一个订了六种的人第二天看到 20 条,
              得先知道这是上限、不是"只找到这些"。
              ★ 数只报服务端给的 `todayCap`;**席位数不反推** —— `clamp(seats×5,10,100)`
              在两个端点上都不可逆(20 条可能是 4 个席位,10 条可能是 1 个也可能是 2 个),
              写一个猜的席位数比不写更坏。 */}
          {view.todayCap != null && (
            <p className="mt-0.5 text-2xs text-muted-foreground">
              今日商机每天最多{" "}
              <span className="font-semibold text-foreground">{view.todayCap}</span>{" "}
              条（按你的在职席位数定；订得再多也不会淹了你）
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={onRecommend} disabled={recommending}>
          {recommending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          重新推断（免费）
        </Button>
      </div>

      {/* ── 一样料都没有:这条路径不是边角,是多数租户第一次进来的画面 ───────── */}
      {noFuel && (
        <p className="mt-3 border-t pt-3 text-xs leading-relaxed text-muted-foreground">
          <span className="font-semibold text-foreground">推不出来是因为还没喂料，不是坏了。</span>
          知识库里一篇都没有、行业包也没铸，AI 没有依据说你该去哪儿找，所以下面全是「未推断」。
          你可以先
          <Link to="/knowledge" className="mx-1 font-semibold text-primary hover:underline">
            传几篇资料
          </Link>
          再回来，也可以直接在「更多」里自己挑一个先试一次。
        </p>
      )}
    </section>
  );
}
